import { useEffect, useMemo, useRef, useState } from "react";
import { useSampleAllocations, useSetSampleAllocations } from "@/api/sample-requests";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { CloseButton } from "@/components/base/buttons/close-button";
import { error, success } from "@/providers/ToastProvider";
import {
  buildAllocationsTemplate,
  buildImportRows,
  classifyImportRow,
  type ImportRow,
  readAllocationsFile,
} from "./allocations-csv";
import { AllocationsManualStep } from "./allocations-manual-step";

const downloadCsv = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export function SetAllocationsModal({
  open,
  onOpenChange,
  focusProductId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  focusProductId?: string;
}) {
  const { data } = useSampleAllocations();
  const { mutateAsync: setAllocations, isPending } = useSetSampleAllocations();
  const [drafts, setDrafts] = useState<Record<string, number>>({});
  const [importRows, setImportRows] = useState<ImportRow[] | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const items = useMemo(() => data ?? [], [data]);
  const capByProductId = useMemo(() => new Map(items.map((i) => [i.productId, i.monthlyCap])), [items]);

  useEffect(() => {
    if (!open) return;
    setDrafts(Object.fromEntries(items.map((i) => [i.productId, i.monthlyCap])));
    setImportRows(null);
    setFileName(null);
  }, [open, items]);

  const buildChanges = (next: Record<string, number>) =>
    items.flatMap((i) => {
      const value = next[i.productId] ?? i.monthlyCap;
      return value === i.monthlyCap ? [] : [{ productId: i.productId, monthlyCap: value }];
    });

  const commit = async (next: Record<string, number>) => {
    const changes = buildChanges(next);
    if (changes.length === 0) {
      onOpenChange(false);
      return;
    }
    try {
      await setAllocations(changes);
      success("Allocations updated");
      onOpenChange(false);
    } catch {
      error("Failed to update allocations");
    }
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const records = await readAllocationsFile(file);
      if (records.length === 0) {
        error("No rows found in the file");
        return;
      }
      setImportRows(buildImportRows(records, items));
      setFileName(file.name);
    } catch {
      error("Could not read the file");
    }
    e.target.value = "";
  };

  const handleSave = () => {
    if (importRows !== null) {
      const next = { ...drafts };
      for (const row of importRows) {
        const status = classifyImportRow(row, capByProductId);
        if (status.status === "valid") next[status.productId] = status.units;
      }
      void commit(next);
    } else {
      void commit(drafts);
    }
  };

  return (
    <ModalOverlay isOpen={open} onOpenChange={onOpenChange} isDismissable>
      <Modal>
        <Dialog>
          <div className="relative flex max-h-[80vh] w-full max-w-[44rem] flex-col overflow-hidden rounded-2xl bg-primary">
            <CloseButton
              onClick={() => onOpenChange(false)}
              theme="light"
              size="lg"
              className="absolute top-3 right-3"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              className="hidden"
              onChange={(e) => void handleFileInputChange(e)}
            />
            <AllocationsManualStep
              items={items}
              drafts={drafts}
              importRows={importRows}
              fileName={fileName}
              capByProductId={capByProductId}
              isPending={isPending}
              focusProductId={focusProductId}
              onDraftChange={(productId, value) => setDrafts((prev) => ({ ...prev, [productId]: value }))}
              onImportRowChange={(key, units) =>
                setImportRows((prev) => prev?.map((r) => (r.key === key ? { ...r, units } : r)) ?? prev)
              }
              onImport={() => fileInputRef.current?.click()}
              onRemoveFile={() => {
                setImportRows(null);
                setFileName(null);
              }}
              onDownloadTemplate={() => downloadCsv(buildAllocationsTemplate(items), "sample-allocations-template.csv")}
              onCancel={() => onOpenChange(false)}
              onSave={handleSave}
            />
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
